import React, { useState, useEffect } from 'react';
import { Box, useTheme, Typography } from '@mui/material';
import { Navigate, useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { FourPaneLayout } from '../components/layout/FourPaneLayout';
import { Header } from '../components/layout/Header';
import { NavigationSidebar } from '../components/chat/Sidebar/NavigationSidebar';
import { InboxSidebar } from '../components/chat/Sidebar/InboxSidebar';
import { ChatArea } from '../components/chat/ChatArea/ChatArea';
import { ProfilePanel } from '../components/chat/ProfileSidebar/ProfilePanel';
import { useResponsiveLayout } from '../hooks/useResponsiveLayout';
import { usePermissions } from '../components/RoleBasedRoute';
import socketClient from '../utils/socket';
import { useCustomer } from '../hooks/useCustomer';
import { useNotifications } from '../hooks/useNotifications';
import { ChatRoom, Customer, Message } from '../types';
import apiClient from '../utils/api';
import { devLog } from '../utils/logger';

interface NewChatInterfaceState {
  selectedChatId: string | null;
  selectedCategory: string;
  chats: ChatRoom[];
  loading: boolean;
  error: string | null;
  isInRoom: boolean; // 入室中かどうか
}

// エラーバウンダリーコンポーネント
const ErrorFallback: React.FC<{ error: Error }> = ({ error }) => {
  return (
    <Box sx={{ 
      height: '100vh', 
      display: 'flex', 
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      p: 3,
      backgroundColor: '#f5f5f5'
    }}>
      <Box sx={{ textAlign: 'center', maxWidth: 500 }}>
        <Typography variant="h5" color="error" gutterBottom>
          エラーが発生しました
        </Typography>
        <Typography variant="body1" paragraph>
          新UIの読み込み中にエラーが発生しました。
        </Typography>
        <Typography variant="body2" color="text.secondary">
          エラー内容: {error.message}
        </Typography>
        <Box sx={{ mt: 3 }}>
          <button onClick={() => window.location.reload()}>
            ページを再読み込み
          </button>
        </Box>
      </Box>
    </Box>
  );
};

export const NewChatInterface: React.FC = () => {
  // デバッグログ（開発環境のみ）
  devLog('🎯 NewChatInterface レンダリング開始');
  
  const theme = useTheme();
  const { adminUser, hasPermission, isAdmin, isOperator } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();
  
  // NavigationSidebarの統計更新用のref
  const navigationRefreshRef = React.useRef<(() => void) | null>(null);
  
  devLog('👤 Permission状態:', { adminUser, isAdmin: isAdmin(), isOperator: isOperator() });
  const {
    layoutConfig,
    activePane,
    switchToPane,
    toggleLeftPane,
    toggleRightPane,
    createLeftResizeHandler,
    createRightResizeHandler,
  } = useResponsiveLayout({
    defaultLeftWidth: 280,
    defaultRightWidth: 320,
  });

  const [state, setState] = useState<NewChatInterfaceState>({
    selectedChatId: null,
    selectedCategory: 'all',
    chats: [],
    loading: false,
    error: null,
    isInRoom: false,
  });

  // ルームロック状態の同期（簡易実装: グローバルイベント購読）
  useEffect(() => {
    const onLockUpdate = (e: any) => {
      const { roomId, locked, owner } = (e as CustomEvent).detail || {};
      const selfId = (window as any).CSCHAT_SOCKET_ID;
      setState(prev => {
        const target = prev.chats.find(c => c.id === roomId);
        const nextLockedBy = locked ? owner : undefined;
        const currentLockedBy = target ? (target as any).locked_by : undefined;
        const shouldUpdateChat = target ? currentLockedBy !== nextLockedBy : false;
        const shouldDeselect = locked && roomId === prev.selectedChatId && owner && owner !== selfId;

        if (!shouldUpdateChat && !shouldDeselect) {
          return prev; // 変化なしなら再レンダを発生させない
        }

        return {
          ...prev,
          selectedChatId: shouldDeselect ? null : prev.selectedChatId,
          chats: shouldUpdateChat
            ? prev.chats.map(c => c.id === roomId ? ({ ...c, locked_by: nextLockedBy } as any) : c)
            : prev.chats,
        };
      });
    };
    // 既存のsocket.tsが window に配信するイベントを購読（重複接続は作らない）
    window.addEventListener('rt:roomLockUpdate', onLockUpdate as EventListener);
    return () => window.removeEventListener('rt:roomLockUpdate', onLockUpdate as EventListener);
  }, []);

  // WebSocket接続を有効化（認証後のみ、初回のみ）
  useEffect(() => {
    if (adminUser) {
      socketClient.connect_socket();
    }
    // adminUserが変わっても再接続しない（依存配列を空にする）
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ここではページ全体の接続開始のみ。詳細は ChatArea で処理

  // 顧客管理統合
  const customer = useCustomer({
    customerId: state.selectedChatId, // チャットIDから顧客情報を生成
    autoLoad: true,
    onUpdate: (updatedCustomer) => {
      devLog('顧客情報が更新されました:', updatedCustomer);
    },
    onError: (error) => {
      // 顧客情報取得エラーは無視（顧客管理機能未実装のため）
      devLog('顧客情報取得失敗（無視）:', error);
    },
  });

  // 表示用の顧客情報はチャットルームの参加者情報を優先（API取得の揺らぎを排除）
  const selectedChat = React.useMemo(
    () => state.chats.find(c => c.id === state.selectedChatId) || null,
    [state.selectedChatId, state.chats]
  );

  const displayCustomer = React.useMemo(() => {
    if (!selectedChat) return null;
    return {
      id: selectedChat.id,
      name: selectedChat.participant_name || 'お客様',
      email: selectedChat.participant_email || '',
      phone: selectedChat.participant_phone || '',
      membershipLevel: 'regular' as const,
      lastSeen: new Date(selectedChat.updated_at || selectedChat.created_at),
      customTags: (selectedChat as any).tags || [],
    };
  }, [selectedChat]);

  // 通知システム（完全無効化）
  const notifications = {
    unreadCount: 0,
    hasUnread: false,
    permission: 'default' as NotificationPermission,
    showNotification: () => {},
    requestPermission: async () => 'default' as NotificationPermission,
    clearUnread: () => {},
  };
  devLog('🔔 useNotifications完全無効化中');

  // チャット一覧の読み込み
  const loadChats = React.useCallback(async (category?: string) => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));
      
      // カテゴリーIDを解析
      const categoryFilter = category || state.selectedCategory;
      devLog('🔍 カテゴリーフィルター:', categoryFilter);
      
      let response: any;
      
      // オペレーターフィルター: operator:id または operator:name:xxx
      if (categoryFilter.startsWith('operator:')) {
        const operatorKey = categoryFilter.replace('operator:', '');
        devLog('👤 オペレーターフィルター:', operatorKey);
        
        // 'name:xxx' の場合は名前で検索、それ以外はIDで検索
        if (operatorKey.startsWith('name:')) {
          const operatorName = operatorKey.replace('name:', '');
          response = await (apiClient as any).getChatRoomsByOperator(operatorName, true); // nameフラグ
        } else {
          response = await (apiClient as any).getChatRoomsByOperator(operatorKey, false); // IDで検索
        }
      }
      // タグフィルター: tag:id または tag-id
      else if (categoryFilter.startsWith('tag:') || categoryFilter.startsWith('tag-')) {
        const tagId = categoryFilter.replace(/^tag[:-]/, '');
        devLog('🏷️ タグフィルター:', tagId);
        response = await (apiClient as any).getChatRoomsByTag(tagId);
      }
      // 固定タグフィルター: fixedtag:optionId（全チャット取得後、InboxSidebarでフィルタ）
      else if (categoryFilter.startsWith('fixedtag:')) {
        devLog('🏷️ 固定タグフィルター:', categoryFilter);
        response = await apiClient.getChatRooms(1, 1000);
      }
      // ステータスフィルター
      else {
        // 「全て」「完了」「削除」は複数ステータスを含むため全件取得してフロント側でフィルタ
        const needsAllData = categoryFilter === 'all' || categoryFilter === 'completed' || categoryFilter === 'uncompleted';
        const statusParam = needsAllData ? undefined : categoryFilter;
        devLog('📊 ステータスフィルター:', statusParam, needsAllData ? '(全件取得→フロントフィルタ)' : '');
        response = await apiClient.getChatRooms(1, 1000, statusParam);
      }
      
      if (response.success && response.chatRooms) {
        devLog('📊 チャット一覧取得:', response.chatRooms.length, '件');
        devLog('🔒 ロック中のルーム:', response.chatRooms.filter((c: any) => c.locked_by).length, '件');
        
        setState(prev => ({ 
          ...prev, 
          chats: response.chatRooms,
          loading: false 
        }));
      } else {
        setState(prev => ({ 
          ...prev, 
          error: 'チャット一覧の取得に失敗しました',
          loading: false 
        }));
      }
    } catch (error) {
      console.error('チャット一覧取得エラー:', error);
      setState(prev => ({ 
        ...prev, 
        error: 'チャット一覧の取得中にエラーが発生しました',
        loading: false 
      }));
    }
  }, [state.selectedCategory]);

  // チャット選択ハンドラー（ロック競合の確認はChatArea入室時に一本化）
  const handleChatSelect = async (chatId: string) => {
    setState(prev => ({ ...prev, selectedChatId: chatId }));
    
    // モバイル・タブレットでは中央ペインに切り替え
    if (layoutConfig.isMobile || layoutConfig.isTablet) {
      switchToPane('center');
    }

    // 顧客情報はuseCustomerフックが自動的に処理
  };

  // カテゴリー選択ハンドラー
  const handleCategorySelect = React.useCallback((categoryId: string) => {
    setState(prev => {
      if (prev.selectedCategory === categoryId) return prev; // 変化なしなら更新しない
      return { ...prev, selectedCategory: categoryId };
    });
    // カテゴリが変わった場合、新しいカテゴリで再取得
    loadChats(categoryId);
  }, [loadChats]);

  // 検索機能は無効化

  // 管理機能遷移（adminのみ）
  const goToSettings = () => {
    if (isAdmin()) navigate('/admin/settings');
  };
  const goToAnalytics = () => {
    if (isAdmin()) navigate('/admin/analytics');
  };
  const goToAccounts = () => {
    // サイドメニュー側で admin のみ表示しているため、ここでは無条件で遷移
    navigate('/admin/accounts');
  };

  // 初期データ読み込み（認証済みの場合のみ実行）
  const [hasLoadedInitialData, setHasLoadedInitialData] = React.useState(false);
  
  useEffect(() => {
    // adminUserがない場合は何もしない（認証チェックでリダイレクトされる）
    if (!adminUser || hasLoadedInitialData) {
      return;
    }

    const init = async () => {
      setHasLoadedInitialData(true);
      await loadChats(); // ロック情報も含めて取得済み

      // クエリパラメータからroom/categoryを検出して自動選択
      try {
        const params = new URLSearchParams(location.search);
        const roomIdFromQuery = params.get('room');
        const categoryFromQuery = params.get('category');
        if (categoryFromQuery) {
          setState(prev => ({ ...prev, selectedCategory: categoryFromQuery }));
          loadChats(categoryFromQuery);
        }
        if (roomIdFromQuery) {
          setState(prev => ({ ...prev, selectedChatId: roomIdFromQuery }));
          if (layoutConfig.isMobile || layoutConfig.isTablet) {
            switchToPane('center');
          }
        }
      } catch {}
    };
    init();
  }, [adminUser, hasLoadedInitialData, loadChats, layoutConfig.isMobile, layoutConfig.isTablet, location.search, switchToPane]);

  // 既読クリアイベントで対象チャットの未読バッジを即時0に反映
  useEffect(() => {
    const onRoomAllRead = (e: Event) => {
      const detail = (e as CustomEvent).detail as { roomId: string };
      if (!detail?.roomId) return;
      setState(prev => ({
        ...prev,
        chats: prev.chats.map(chat => chat.id === detail.roomId ? { ...chat, unread_count: 0 } : chat)
      }));
    };
    window.addEventListener('rt:roomAllRead', onRoomAllRead as EventListener);
    return () => window.removeEventListener('rt:roomAllRead', onRoomAllRead as EventListener);
  }, []);

  // 30秒ごとにチャット一覧とロック情報を自動更新
  useEffect(() => {
    if (!hasLoadedInitialData) return;
    
    const intervalId = setInterval(() => {
      devLog('🔄 定期更新: チャット一覧とロック情報を再取得');
      loadChats();
    }, 30000); // 30秒ごと
    
    return () => clearInterval(intervalId);
  }, [hasLoadedInitialData, loadChats]);

  // レスポンシブでの戻るボタン処理
  const handleBackToList = () => {
    if (layoutConfig.isMobile) {
      switchToPane('left');
    }
  };

  const handleShowCustomerProfile = () => {
    if (layoutConfig.isMobile || layoutConfig.isTablet) {
      switchToPane('right');
    }
  };

  // 認証チェック - ログインしていない場合はログイン画面へ
  if (!adminUser) {
    devLog('🚫 未認証のため /login にリダイレクト');
    return <Navigate to="/login" replace />;
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* ヘッダー */}
      <Header
        title="お問い合わせ画面"
        showMenuButton={layoutConfig.isMobile}
        onMenuToggle={toggleLeftPane}
      />

      {/* メインコンテンツエリア（ヘッダー分を差し引いた固定領域）*/}
      <Box sx={{ 
        flex: 1, 
        mt: '60px',
        minHeight: 0, // 子のoverflowを有効にするために必要
      }}>
        <FourPaneLayout
          navigationPane={
            <Box 
              sx={{
                height: '100%',
                position: 'relative'
              }}
            >
              <NavigationSidebar
                selectedCategory={state.selectedCategory}
                onCategorySelect={handleCategorySelect}
                compactMode={false}
                onChatRoomCreate={() => devLog('チャットルーム作成')}
                onOperatorManage={() => devLog('オペレーター管理')}
                onTagManage={() => devLog('タグ管理')}
                onSystemSettings={goToSettings}
                onAnalytics={goToAnalytics}
                onAccounts={goToAccounts}
                onRefreshProvided={(refreshFn: () => void) => {
                  navigationRefreshRef.current = refreshFn;
                }}
              />
              {state.isInRoom && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.3)',
                    zIndex: 1000,
                    cursor: 'not-allowed'
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                />
              )}
            </Box>
          }
          inboxPane={
            <Box 
              sx={{
                height: '100%',
                position: 'relative'
              }}
            >
              <InboxSidebar
                chats={state.chats as any}
                selectedChatId={state.selectedChatId}
                onChatSelect={handleChatSelect}
                loading={state.loading}
                error={state.error}
                onRefresh={loadChats}
                selectedCategory={state.selectedCategory}
              />
              {state.isInRoom && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.3)',
                    zIndex: 1000,
                    cursor: 'not-allowed'
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                />
              )}
            </Box>
          }
          chatPane={
            <ChatArea
              selectedChatId={state.selectedChatId}
              selectedCustomer={displayCustomer || customer.customer}
              roomTitle={selectedChat?.title}
              initialRoomStatus={selectedChat?.status}
              euLoggedIn={selectedChat?.eu_logged_in}
              onRoomStatusChanged={(newStatus) => {
                setState(prev => ({
                  ...prev,
                  chats: prev.chats.map(c => c.id === state.selectedChatId ? { ...c, status: newStatus as any } : c)
                }));
                // ステータス変更時にNavigationSidebarの統計を更新 + チャット一覧を再読み込み
                devLog('🔄 ステータス変更検知 → NavigationSidebar統計を更新 + チャット一覧を再読み込み');
                navigationRefreshRef.current?.();
                loadChats(); // チャット一覧も更新
              }}
              onBackToList={handleBackToList}
              onShowCustomerProfile={handleShowCustomerProfile}
              showBackButton={layoutConfig.isMobile}
              showCustomerButton={layoutConfig.isMobile || layoutConfig.isTablet}
              onRoomEnter={() => setState(prev => ({ ...prev, isInRoom: true }))}
              onRoomLeave={() => {
                // 退室時: isInRoomをfalseにし、チャット一覧のアクティブ状態も外す
                setState(prev => ({ ...prev, isInRoom: false, selectedChatId: null }));
              }}
            />
          }
          profilePane={
            <ProfilePanel
              customer={displayCustomer || customer.customer}
              selectedChatId={state.selectedChatId}
              onCustomerUpdate={customer.updateCustomer}
            />
          }
          navigationWidth={220}
          inboxWidth={layoutConfig.leftWidth}
          profileWidth={layoutConfig.rightWidth}
        />
      </Box>
    </Box>
  );
};

export default NewChatInterface;
